import { useCallback, useEffect, useRef } from "react";

import { useTransformContext } from "react-zoom-pan-pinch";

import { unrotatePoint, type Point } from "../utils";

interface UseDragItemProps {
  origin: "center" | "bottom center";
  getRotation?: () => number;
  onDragStart?: (e: PointerEvent, point: Point) => void;
  onDragEnd?: (e: PointerEvent, point: Point) => void;
  onDragChange?: (point: Point) => void;
  disabled?: boolean;
}

interface UseDragItemType<T> {
  target: React.RefObject<T | null>;
}

const useDragItem = <T extends HTMLElement>(props: UseDragItemProps): UseDragItemType<T> => {
  const { origin, getRotation, onDragStart, onDragEnd, onDragChange, disabled } = props;

  const { contentComponent: container } = useTransformContext();

  const target = useRef<T | null>(null);

  const offset = useRef<Point | null>(null);

  const position = useRef<Point | null>(null);
  const isDragging = useRef<boolean>(false);

  const currentPositionChange = useRef(onDragChange);
  const currentPositionStart = useRef(onDragStart);
  const currentPositionEnd = useRef(onDragEnd);
  const onChangeRefId = useRef<number | undefined>(undefined);

  currentPositionChange.current = useCallback(
    (point: Point) => {
      onChangeRefId.current = onDragChange
        ? requestAnimationFrame(() => {
            onDragChange(point);
          })
        : undefined;
    },
    [onDragChange],
  );
  currentPositionStart.current = onDragStart;
  currentPositionEnd.current = onDragEnd;

  const getContainerDimensions = useCallback(() => {
    if (!container) return { width: 0, height: 0 };
    const rect = container.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height,
    };
  }, [container]);

  const getPointFromEvent = useCallback(
    (e: PointerEvent): Point => {
      const rect = container?.getBoundingClientRect();
      return {
        x: e.clientX - (typeof rect?.left === "number" ? rect.left : 0),
        y: e.clientY - (typeof rect?.top === "number" ? rect.top : 0),
      };
    },
    [container],
  );

  const cancelCurrentDragging = useCallback(() => {
    position.current = null;
    isDragging.current = false;
    if (typeof onChangeRefId.current === "number") {
      cancelAnimationFrame(onChangeRefId.current);
    }
  }, []);

  const getPoint = useCallback(
    (position: Point, offset: Point) => {
      const itemWidth = target.current?.offsetWidth ?? 0;
      const itemHeight = target.current?.offsetHeight ?? 0;

      const { width, height } = getContainerDimensions();
      const anchor = unrotatePoint(
        {
          x: position.x - offset.x + itemWidth / 2,
          y: position.y - offset.y + (origin === "center" ? itemHeight / 2 : itemHeight),
        },
        { x: width / 2, y: height / 2 },
        getRotation?.() ?? 0,
      );
      const newLeft = Math.min(100, Math.max(0, (anchor.x * 100) / width));
      const newTop = Math.min(100, Math.max(0, (anchor.y * 100) / height));
      return { x: newLeft, y: newTop };
    },
    [getContainerDimensions, getRotation, origin],
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (position.current && offset.current) {
        const { x, y } = getPoint(position.current, offset.current);

        if (!isDragging.current) {
          if (currentPositionStart.current) {
            currentPositionStart.current(e, { x, y });
          }
          isDragging.current = true;
        }
        requestAnimationFrame(() => {
          if (target.current) {
            target.current.style.position = "absolute";
            target.current.style.left = `${x}%`;
            target.current.style.top = `${y}%`;
          }
        });
        currentPositionChange.current?.({ x, y });
      } else {
        cancelCurrentDragging();
      }
    },
    [getPoint, cancelCurrentDragging],
  );

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      position.current = getPointFromEvent(e);
      handlePointerMove(e);
    },
    [getPointFromEvent, handlePointerMove],
  );

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      target.current?.releasePointerCapture(e.pointerId);

      if (position.current && offset.current) {
        currentPositionEnd.current?.(e, getPoint(position.current, offset.current));
      }
      cancelCurrentDragging();

      target.current?.removeEventListener("pointermove", onPointerMove);
      target.current?.removeEventListener("pointerup", onPointerUp);
      target.current?.removeEventListener("pointercancel", onPointerCancel);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- onPointerCancel is defined below and would create a cycle
    [cancelCurrentDragging, getPoint, onPointerMove],
  );

  const onPointerCancel = useCallback(
    (e: PointerEvent) => {
      target.current?.releasePointerCapture(e.pointerId);
      cancelCurrentDragging();

      target.current?.removeEventListener("pointermove", onPointerMove);
      target.current?.removeEventListener("pointerup", onPointerUp);
      target.current?.removeEventListener("pointercancel", onPointerCancel);
    },
    [cancelCurrentDragging, onPointerMove, onPointerUp],
  );

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      // Accept left button (mouse) and any touch/pen input
      if (e.button !== 0) return;

      e.preventDefault();

      const targetRect = target.current?.getBoundingClientRect();
      offset.current = {
        x: e.clientX - (typeof targetRect?.left === "number" ? targetRect.left : 0),
        y: e.clientY - (typeof targetRect?.top === "number" ? targetRect.top : 0),
      };

      // Capture the pointer so all subsequent pointermove/pointerup events
      // are dispatched to this element even if the pointer leaves it.
      // This also prevents Radix DismissableLayer from intercepting the drag.
      target.current?.setPointerCapture(e.pointerId);

      target.current?.addEventListener("pointermove", onPointerMove);
      target.current?.addEventListener("pointerup", onPointerUp);
      target.current?.addEventListener("pointercancel", onPointerCancel);
    },
    [onPointerMove, onPointerUp, onPointerCancel],
  );

  useEffect(() => {
    const el = target.current;
    if (!disabled && el) {
      // passive: false is required so that e.preventDefault() in onPointerDown
      // can suppress the browser's default scroll/pan behaviour during drag.
      el.addEventListener("pointerdown", onPointerDown, { passive: false });
    }

    return () => {
      el?.removeEventListener("pointerdown", onPointerDown);
      el?.removeEventListener("pointermove", onPointerMove);
      el?.removeEventListener("pointerup", onPointerUp);
      el?.removeEventListener("pointercancel", onPointerCancel);
    };
  }, [onPointerDown, onPointerMove, onPointerUp, onPointerCancel, disabled]);

  return {
    target,
  };
};

export { useDragItem };
export type { UseDragItemProps, UseDragItemType };
